import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Helmet } from "react-helmet";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";
import Banner from "../assets/images/banners/Event.webp";
import ModalToggler from "../components/UpcomingEvents/ModalToggler";
import ModalContent from "../components/UpcomingEvents/ModalContent";

const normalizeBaseUrl = (baseUrl) => {
  if (!baseUrl) return "";
  return baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
};

const normalizePathPrefix = (prefix) => {
  if (!prefix) return "";
  if (!prefix.startsWith("/")) return `/${prefix}`;
  return prefix;
};

const EventsBackendImplement = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resolvedApiBaseUrl, setResolvedApiBaseUrl] = useState("");

  const apiBaseUrlCandidates = useMemo(() => {
    const base = normalizeBaseUrl(process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:2902");
    const version = normalizePathPrefix(process.env.REACT_APP_API_VERSION || "");
    const primary = `${base}${version}`;

    if (version) return [primary];
    return [primary, `${base}/api/v1`, `${base}/api`];
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    setError("");

    try {
      let lastError = null;
      let allEvents = [];

      for (const candidate of apiBaseUrlCandidates) {
        try {
          console.log(`EventsBackendImplement: Trying API candidate: ${candidate}`);
          
          // Fetch all events without status filter, handling pagination
          let page = 1;
          let totalPages = 1;
          
          do {
            console.log(`EventsBackendImplement: Fetching page ${page} of ${totalPages}...`);
            const allRes = await axios.get(`${candidate}/event/live`, { 
              params: { page: page }
            });
            
            console.log(`EventsBackendImplement: Page ${page} API response:`, {
              success: allRes.data.success,
              result: allRes.data.result,
              page: allRes.data.page,
              totalPages: allRes.data.totalPages,
              total: allRes.data.total
            });
            
            const pageEvents = allRes?.data?.result || [];
            console.log(`EventsBackendImplement: Page ${page} returned ${pageEvents.length} events`);
            
            allEvents = [...allEvents, ...pageEvents];
            
            totalPages = allRes?.data?.totalPages || 1;
            console.log(`EventsBackendImplement: Total pages available: ${totalPages}, fetched pages: ${page}`);
            
            page++;
            
            // Safety check to prevent infinite loops
            if (page > 10) {
              console.log("EventsBackendImplement: Safety check - stopping at page 10");
              break;
            }
          } while (page <= totalPages);
          
          console.log("EventsBackendImplement: Total events fetched:", allEvents.length);
          
          // Remove duplicate events by ID
          const uniqueEvents = allEvents.filter((event, index, self) => 
            index === self.findIndex((e) => e._id === event._id)
          );
          
          console.log("EventsBackendImplement: Unique events after deduplication:", uniqueEvents.length);
          
          // Separate events into upcoming and past based on start_date
          const currentDate = new Date();
          const upcoming = uniqueEvents.filter(event => {
            // Include recurring events (no start_date) as upcoming
            if (!event.start_date) return true;
            return new Date(event.start_date) >= currentDate;
          });
          const past = uniqueEvents.filter(event => {
            // Only include events with start_date in past
            if (!event.start_date) return false;
            return new Date(event.start_date) < currentDate;
          });
          
          console.log("EventsBackendImplement: Upcoming events:", upcoming.length);
          console.log("EventsBackendImplement: Past events:", past.length);
          
          setResolvedApiBaseUrl(candidate);
          setUpcomingEvents(upcoming);
          setPastEvents(past);
          lastError = null;
          break;
        } catch (innerErr) {
          console.log("EventsBackendImplement: API candidate failed:", innerErr);
          lastError = innerErr;
        }
      }

      if (lastError) throw lastError;
    } catch (e) {
      console.error("EventsBackendImplement: Final error:", e);
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load events from backend. Check backend URL/API version and that the server is running.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEvents();
  }, []);

  const toYmdFromIso = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Recurring";
    const localDate = new Date(dateString + "T00:00:00");
    const options = { year: "numeric", month: "long", day: "numeric" };
    return localDate.toLocaleDateString("en-US", options);
  };

  const mapBackendEventToUiEvent = (event) => {
    const id = event?._id || event?.id;
    const date = event?.type === "recurring" ? "" : toYmdFromIso(event?.start_date);
    return {
      id,
      title: event?.title || "",
      image: event?.image?.url || "",
      date,
    };
  };

  return (
    <>
      <Helmet>
        <title>Events | Huntsville Islamic Center</title>
      </Helmet>

      <section id="website-responsive" className="px-0">
        <section className="container-fluid bg-common-banner" style={{ backgroundImage: `url(${Banner})` }}>
          <Header />
          <div className="fs-banner-title text-white fw-500 text-center py-5">
            <div className="py-md-5 mb-md-5">
              Events
              <div className="py-3 py-lg-4" />
            </div>
          </div>
        </section>

        <div className="py-4 py-md-5" />

        <section id="events" className="container-fluid">
          {loading ? <p className="text-center fs-ss-18 fw-500">Loading...</p> : null}
          {error ? <p className="text-center fs-ss-18 fw-500">{error}</p> : null}

          {!loading && !error ? (
            <Tabs>
              <TabList className="nav nav-tabs justify-content-center fw-600 fs-ss-20">
                <Tab className="nav-item px-4 center">
                  <a className="nav-link" href="#">
                    Upcoming Events
                  </a>
                </Tab>
                <div className="py-2 d-block d-md-none" />
                <Tab className="nav-item px-4 center">
                  <a className="nav-link" href="#">
                    Past Events
                  </a>
                </Tab>
              </TabList>

              <TabPanel className="mt-5">
                <h2 className="my-4 text-center">Upcoming Events</h2>
                {upcomingEvents.length > 0 ? (
                  <div className="row col-12 mx-auto justify-content-center px-lg-5 gap-4">
                    {upcomingEvents.map(mapBackendEventToUiEvent).map((event) => (
                      <div key={event.id} className="col-md-5 col-lg-3">
                        <ModalToggler Img={event.image} Id={event.id} Title={event.title} Date={formatDate(event.date)} />
                        <ModalContent Img={event.image} Id={event.id} Title={event.title} Date={formatDate(event.date)} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center fs-ss-18 fw-500">No upcoming events.</p>
                )}
              </TabPanel>

              <TabPanel className="mt-5">
                <h2 className="my-4 text-center">Past Events</h2>
                {pastEvents.length > 0 ? (
                  <div className="row col-12 mx-auto justify-content-center px-lg-5 gap-4">
                    {pastEvents.map(mapBackendEventToUiEvent).map((event) => (
                      <div key={event.id} className="col-md-5 col-lg-3">
                        <ModalToggler Img={event.image} Id={event.id} Title={event.title} Date={formatDate(event.date)} />
                        <ModalContent Img={event.image} Id={event.id} Title={event.title} Date={formatDate(event.date)} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center fs-ss-18 fw-500">No past events.</p>
                )}
              </TabPanel>
            </Tabs>
          ) : null}
        </section>

        <div className="py-4 py-md-5" />

        <Footer />
      </section>
    </>
  );
};

export default EventsBackendImplement;
